<template>
  <div class="product-list pa-4">
    <div class="d-flex align-center mb-4">
      <!-- 搜索框 -->
      <div class="search-bar" style="width: 300px">
        <v-icon class="mr-2" color="rgba(255, 255, 255, 0.7)">mdi-magnify</v-icon>
        <v-text-field
          v-model="searchTerm"
          placeholder="搜索批号、编号、名称"
          hide-details
          variant="outlined"
          bg-color="transparent"
          color="white"
          class="search-input"
        />
      </div>
    </div>

    <!-- 产品列表 -->
    <v-data-table
      v-model="selected"
      :headers="headers"
      :options="options"
      :items="filteredProducts"
      :loading="loading"
      show-select
      item-value="id"
      class="elevation-1 mb-16"  
      :page="options.page"
      @update:page="onPageChange"
      :items-per-page-text="'每页项目数'"
    >
      <template #item.dimensions="{ item }">
        {{ formatDimensions(item) }}
      </template>

      <template #item.drawCount="{ item }">
        {{ item.drawCount }}
      </template>

      <template #item.actions="{ item }">
        <v-btn
          variant="flat"
          size="small"
          color="primary"
          @click="toDraws(item)"
        >
          <!-- @click="() => {
            console.log('跳转参数:', {
              contractId: route.params.contractId,
              productId: item.id
            })
            router.push(`/contract/${route.params.contractId}/product/${item.id}/images`)
          }" -->
          查看图纸
        </v-btn>
      </template>

      <template #no-data>
        <div class="text-center py-4">
          {{ error || '未找到产品' }}
        </div>
      </template>
    </v-data-table>

    <!-- 底部固定打印按钮 -->
    <div class="print-button-container">
      <v-btn
        variant="text"
        :disabled="selected.length === 0"
        @click="printSelectedDrawings"
        class="print-button"
      >
        <v-icon left>mdi-printer</v-icon>
        打印选中图纸 ({{ selected.length }})
      </v-btn>
    </div>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      location="top"
    >
      {{ snackbar.text }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="snackbar.show = false"
        >
          关闭
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
import { orderApi } from '@/services/api'
import { handlePrint } from '@/utils/print'
import { useOrderStore } from '@/stores/order'

interface Product {
  id: number
  no: string
  name: string
  batch: string
  count: string
  width: number
  depth: number
  height: number
  delivery: string
  typeOf: string
  draws: string[]
  drawCount: number
}

const router = useRouter()
const route = useRoute()
const orderStore = useOrderStore()

// 获取路由参数
const contractId = computed(() => {
  const id = route.params.contractId
  console.log('当前路由参数 contractId:', id)
  return id
})

// 响应式状态
const products = ref<Product[]>([])
const selected = ref<Product[]>([])
const loading = ref(false)
let currentPage = ref(1)
const options = ref({page:1});
const error = ref<string | null>(null)
const searchTerm = ref('')
const snackbar = ref({
  show: false,
  text: '',
  color: 'success' as 'success' | 'error' | 'warning'
})

// 显示提示消息
const showMessage = (text: string, color: 'success' | 'error' | 'warning' = 'success') => {
  snackbar.value = {
    show: true,
    text,
    color
  }
}

// 表格列定义
const headers = [
  { title: '批号', key: 'batch' },
  { title: '编号', key: 'no' },
  { title: '名称', key: 'name' },
  { title: '工厂', key: 'typeOf' },
  { title: '数量', key: 'count' },
  { title: '尺寸', key: 'dimensions' ,sortable:false },
  { title: '图纸数', key: 'drawCount' },
  { title: '图纸', key: 'actions' }
]

// 过滤产品列表
const filteredProducts = computed(() => {
  const search = searchTerm.value.toLowerCase()
  return products.value.filter(product =>
    product.batch?.toLowerCase().includes(search) ||
    product.no?.toLowerCase().includes(search) ||
    product.name?.toLowerCase().includes(search)
  )
})

// 格式化尺寸
const formatDimensions = (item: Product) => {
  if (!item.width && !item.depth && !item.height) return '-'
  return `${item.width || 0}*${item.depth || 0}*${item.height || 0}`
}

// 获取产品列表
const fetchProducts = async () => {
  if (!contractId.value) {
    console.error('获取产品列表失败：未找到合同ID')
    showMessage('未找到合同ID', 'error')
    return
  }
  
  loading.value = true
  error.value = null  
  try { 
     // 使用store中的数据
    if (!orderStore.orderData) {
      await orderStore.fetchOrderData(Number(contractId.value))
    }
    const orderData = orderStore.orderData

    if (orderData?.itemList) {
      products.value = orderData.itemList.map(item => {
        // 解析 draws 字段
        let draws = item.draws;
        if (typeof draws === 'string') {
          try {
            draws = JSON.parse(draws);
          } catch (e) {
            console.error('解析图纸数据失败:', e);
            draws = [];
          }
        }
        
        // 确保 draws 是数组
        if (!Array.isArray(draws)) {
          draws = draws ? [draws] : [];
        }
        
        return {
          id: item.id,
          no: item.no || '-',
          name: item.name || '-',
          batch: item.batch || '-',
          count: item.count || '-',
          width: item.width || 0,
          depth: item.depth || 0,
          height: item.height || 0,
          delivery: item.delivery || '-',
          typeOf: item.typeOf || '-',
          draws: draws,
          drawCount: draws.length
        };
      });

      const cacheKey = `productList_${contractId.value}`
      const cacheData = sessionStorage.getItem(cacheKey)
      if (cacheData) {
        const data = JSON.parse(cacheData)
        // 只恢复那些不需要重新请求的状态
        options.value.page = data.currentPage 
      }
    }
  } catch (error: any) {
    console.error('获取产品列表失败:', error)
    showMessage(error.message || '获取产品列表失败', 'error')
  } finally {
    loading.value = false
  }
}

const toDraws = (item: any) => {
  const url = `/contract/${route.params.contractId}/product/${item.id}/images`
  window.open(url, '_blank')
  // router.push(`/contract/${route.params.contractId}/product/${item.id}/images`)
}

// 监听路由变化
// 监听路由变化
watch(() => route.params.contractId, fetchProducts, { immediate: true }) 

// 从缓存恢复状态
const restoreFromCache = () => {
  console.log('恢复缓存...')
  const cacheKey = `productList_${contractId.value}`
  const cacheData = sessionStorage.getItem(cacheKey)
  if (cacheData) {
    const data = JSON.parse(cacheData)
    // 只恢复那些不需要重新请求的状态
    searchTerm.value = data.searchTerm 
    selected.value = data.selected || []
    if (data.timestamp && Date.now() - data.timestamp < 30 * 60 * 1000) { // 30分钟内的缓存
      products.value = data.products || []
      options.value.page = data.currentPage 
    }
  }
}

const onPageChange = (page: number) => { 
  console.log('options.value.page=',options.value.page)
  // 在这里执行你需要的操作
  options.value.page = page
  saveCache()
}

// 保存缓存
const saveCache = () => {
  const cacheKey = `productList_${contractId.value}`
  const cacheData = {
    searchTerm: searchTerm.value,
    selected: selected.value,
    products: products.value,
    currentPage: options.value.page,
    timestamp: Date.now()
  }
  console.log('currentPage.value',currentPage.value)
  sessionStorage.setItem(cacheKey, JSON.stringify(cacheData))
}

// 在路由离开前保存状态
onBeforeRouteLeave(() => {
  saveCache()
})

// 监听数据变化自动保存缓存
watch([searchTerm, selected, products], () => {
  saveCache()
}, { deep: true })

// 页面加载时获取数据
onMounted(() => {
  //restoreFromCache()
  console.log('页面加载，当前 contractId:', contractId.value)
  if(products.value.length > 0) return 
  if (contractId.value) {
    fetchProducts()
  } else {
    console.error('未找到合同ID')
    showMessage('未找到合同ID', 'error')
  }
})

onUnmounted(() => {
  saveCache()
})

// 打印选中产品的图纸
const printSelectedDrawings = async () => {
  if (selected.value.length === 0) {
    showMessage('请先选择要打印的产品', 'warning')
    return
  }

  // 从已有数据中获取图纸
  const allDrawings: string[] = []
  const selectedProducts = products.value.filter(p => selected.value.includes(p.id))
  
  for (const product of selectedProducts) {
    console.log('处理产品图纸:', product.no, product.draws)
    if (Array.isArray(product.draws) && product.draws.length > 0) {
      allDrawings.push(...product.draws)
    }
  }

  console.log('收集到的所有图纸:', allDrawings)
  if (allDrawings.length === 0) {
    showMessage('选中的产品没有可打印的图纸', 'warning')
    return
  }

  try {
    console.log('准备打印图纸:', allDrawings)
    await handlePrint(allDrawings)
    showMessage('打印任务已发送')
  } catch (error) {
    console.error('打印失败:', error)
    showMessage('打印失败，请重试', 'error')
  }
}

// 监听选中变化
watch(selected, (newVal) => {
  console.log('选中的产品发生变化:', newVal)
}, { deep: true })

// 监听搜索词变化
watch(searchTerm, () => {
  error.value = null
})
</script>

<style lang="scss" scoped>
.product-list {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  min-height: calc(100vh - 64px);
  background-color: #2B2D33;
}

/* 搜索框样式 */
.search-bar {
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-radius: 4px;
  height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.search-bar:hover {
  border-color: rgba(255, 255, 255, 0.2);
}

.search-bar:focus-within {
  border-color: rgba(255, 255, 255, 0.3);
}

.search-bar :deep(.search-input) {
  .v-field__input {
    color: white !important;
    min-height: 40px !important;
    padding: 0 !important;
  }
  
  .v-field__field {
    padding: 0 !important;
  }
  
  .v-field {
    border-radius: 0;
    background: transparent !important;
  }
  
  .v-field__outline {
    display: none;
  }
  
  input::placeholder {
    color: rgba(255, 255, 255, 0.7) !important;
  }
}
.search-table :deep(th) {
  white-space: nowrap;
}

/* 数据表格样式 */
:deep(.v-data-table) {
  background-color: #2B2D33 !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

:deep(.v-data-table-header) {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

:deep(.v-data-table-header th) {
  color: rgba(255, 255, 255, 0.7) !important;
  font-weight: 500 !important;
  font-size: 0.875rem !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}

:deep(.v-data-table__wrapper > table > tbody > tr) {
  background-color: transparent !important;
  transition: background-color 0.2s ease;
}

:deep(tr:hover) {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

:deep(th:hover) {
  color: rgba(255, 255, 255, 0.9) !important;
}

:deep(.v-data-table__wrapper > table > tbody > tr > td) {
  color: rgba(255, 255, 255, 0.9) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}

/* 分页按钮样式 */
:deep(.v-data-table-footer) {
  background-color: #2B2D33 !important;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7) !important;

  .v-btn {
    &:not(.v-btn--disabled) {
      background: var(--primary-gradient) !important;
      color: white !important;
      border: none !important;
      box-shadow: 0 2px 8px rgba(227, 76, 101, 0.3) !important;

      &:hover {
        box-shadow: 0 4px 12px rgba(227, 76, 101, 0.4) !important;
      }
      
      &:active {
        box-shadow: 0 1px 4px rgba(227, 76, 101, 0.2) !important;
      }
    }
    
    &.v-btn--disabled {
      opacity: 0.5;
      background: linear-gradient(315deg, rgba(227, 76, 101, 0.5), rgba(255, 123, 142, 0.5)) !important;
      color: rgba(255, 255, 255, 0.5) !important;
      box-shadow: none !important;
    }
  }
  
  .v-data-table-footer__items-per-page,
  .v-data-table-footer__info {
    color: rgba(255, 255, 255, 0.7);
  }
}

/* 打印按钮容器 */
.print-button-container {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
}

/* 打印按钮样式 */
.print-button {
  background: var(--primary-gradient) !important;
  color: white !important;
  padding: 0 24px !important;
  height: 48px !important;
  border-radius: 24px !important;
  font-weight: 500 !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 4px 12px rgba(227, 76, 101, 0.3);
  transition: all 0.3s ease;
}

.print-button:hover {
  box-shadow: 0 6px 16px rgba(227, 76, 101, 0.4);
  transform: translateY(-2px);
}

.print-button:disabled {
  opacity: 0.5;
  transform: none;
  box-shadow: none;
}
</style>